import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Loader2, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import PhotoPicker from "@/components/PhotoPicker";

const fmt = (c) => `$${(c / 100).toFixed(2)}`;
const empty = { name: "", description: "", price_cents: 0, image_url: "", category: "general", stock: 1 };

function ProductImagePicker({ value, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.[0]) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Image must be under 15 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Don't set Content-Type explicitly — axios + FormData auto-generates the multipart boundary.
      const r = await api.post("/upload", fd);
      const url = fileUrl(r.data.id);
      onChange(url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div>
      <Label>Product image</Label>
      <div className="mt-2 grid gap-3 sm:grid-cols-[120px_1fr] sm:items-start">
        <div className="grid h-[120px] w-full place-items-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50 sm:w-[120px]">
          {value ? (
            <div className="relative h-full w-full">
              <img src={value} alt="preview" className="h-full w-full object-cover" />
              <button type="button" onClick={() => onChange("")} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80" aria-label="Remove image" data-testid="product-form-image-remove">
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <span className="px-2 text-center text-[11px] uppercase tracking-widest text-slate-400">No image</span>
          )}
        </div>
        <div className="space-y-2">
          <PhotoPicker onFiles={handleFiles} multiple={false} busy={uploading} testIdPrefix="product-form-image" />
          <div className="text-[11px] text-slate-500">Phone camera, photo library, PC or Mac. JPG, PNG, WEBP up to 15 MB.</div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> or paste a URL <div className="h-px flex-1 bg-slate-200" />
          </div>
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com/image.jpg"
            data-testid="product-form-image"
          />
        </div>
      </div>
    </div>
  );
}

function PriceInput({ valueCents, onChangeCents }) {
  // Local string state so the user can type "12.5" without it being reformatted to "12.50"
  // on every keystroke. We only sync up to cents when the input parses cleanly.
  const initial = valueCents != null && valueCents > 0 ? (valueCents / 100).toFixed(2) : "";
  const [text, setText] = useState(initial);

  // Resync if the parent swaps to a different product (e.g. opening another edit dialog).
  useEffect(() => {
    const currentCents = Math.round((parseFloat(text) || 0) * 100);
    if (currentCents !== (valueCents || 0)) {
      setText(valueCents != null && valueCents > 0 ? (valueCents / 100).toFixed(2) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueCents]);

  const handleChange = (raw) => {
    // Allow empty, digits and a single decimal with up to 2 fractional digits
    if (raw === "" || /^\d*(\.\d{0,2})?$/.test(raw)) {
      setText(raw);
      const dollars = parseFloat(raw);
      onChangeCents(isNaN(dollars) ? 0 : Math.round(dollars * 100));
    }
  };

  const handleBlur = () => {
    if (text === "" || text === ".") { setText(""); onChangeCents(0); return; }
    const dollars = parseFloat(text);
    if (isNaN(dollars)) { setText(""); onChangeCents(0); return; }
    setText(dollars.toFixed(2));
    onChangeCents(Math.round(dollars * 100));
  };

  return (
    <div className="relative mt-1">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500">$</span>
      <Input
        type="text"
        inputMode="decimal"
        className="pl-7"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder="0.00"
        data-testid="product-form-price"
      />
    </div>
  );
}

export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/products");
    setProducts(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async () => {
    try {
      if (editing.id) await api.patch(`/products/${editing.id}`, editing);
      else await api.post("/products", editing);
      setEditing(null);
      load();
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  }, [editing, load]);

  const remove = useCallback(async (id) => {
    if (!window.confirm("Delete product?")) return;
    try { await api.delete(`/products/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold text-slate-900">Products</h1>
        <Button onClick={() => setEditing({ ...empty })} data-testid="products-new-button">
          <Plus className="h-4 w-4" /> New product
        </Button>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <div key={p.id} className="dash-surface overflow-hidden" data-testid={`product-admin-${p.id}`}>
            <div className="aspect-[4/3] bg-slate-100">
              {p.image_url && <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.category} · {p.stock} in stock</div>
                </div>
                <div className="font-medium text-slate-900">{fmt(p.price_cents)}</div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`product-edit-${p.id}`}><Pencil className="h-3 w-3" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => remove(p.id)} data-testid={`product-delete-${p.id}`}><Trash2 className="h-3 w-3" /> Delete</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input className="mt-1" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="product-form-name" /></div>
              <div><Label>Description</Label><Textarea rows={3} className="mt-1" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="product-form-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Price (USD)</Label>
                  <PriceInput
                    valueCents={editing.price_cents}
                    onChangeCents={(c) => setEditing({ ...editing, price_cents: c })}
                  />
                </div>
                <div><Label>Stock</Label><Input type="number" className="mt-1" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: parseInt(e.target.value || "0", 10) })} data-testid="product-form-stock" /></div>
              </div>
              <div><Label>Category</Label><Input className="mt-1" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="product-form-category" /></div>
              <ProductImagePicker
                value={editing.image_url}
                onChange={(url) => setEditing({ ...editing, image_url: url })}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} data-testid="product-form-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
